import {
  GHARepoJobTableFilters,
  GHARepositorySelection,
} from "@/components/filters";
import { repoNameWithoutOrgPrefix } from "@/components/filters/utils";
import { GHAJobStats } from "@/hooks/useGithubActionsAnalytics";
import { atom } from "jotai";

export interface CommonFilters {
  selectedRunnerTypes: string[];
  selectedStatuses: string[];
}

// OrgLevelChartFilters are chart filters for org level analytics.
export interface OrgLevelChartFilters extends CommonFilters {
  selectedRepos: GHARepositorySelection[];
}

export interface GHAJobSelection extends GHAJobStats {
  color: string;
}

// RepoLevelChartFilters are chart filters for repo level analytics.
export interface RepoLevelChartFilters extends CommonFilters {
  selectedJobs: GHAJobSelection[];
  selectedBranches: string[];
}

export type GHADataViewType = "job" | "workflow";
export const ghaDataViewAtom = atom<GHADataViewType>("job");

export const defaultOrgChartFilters: OrgLevelChartFilters = {
  selectedRepos: [],
  selectedRunnerTypes: [],
  selectedStatuses: [],
};

export const defaultRepoChartFilters: RepoLevelChartFilters = {
  selectedJobs: [],
  selectedRunnerTypes: [],
  selectedBranches: [],
  selectedStatuses: [],
};

export const ghaOrgChartFiltersAtom = atom(defaultOrgChartFilters);
export const ghaRepoChartFiltersAtom = atom(defaultRepoChartFilters);

export const ghaOrgRepoTableFiltersAtom = atom<GHARepoJobTableFilters>({});
export const ghaRepoJobTableFiltersAtom = atom<GHARepoJobTableFilters>({});

export function getOrgChartQueryParams(
  filters: OrgLevelChartFilters,
): URLSearchParams {
  const params = new URLSearchParams();

  if (filters.selectedRepos.length > 0) {
    filters.selectedRepos.forEach((repo) => {
      const strippedRepoName = repoNameWithoutOrgPrefix(repo.repository);
      if (strippedRepoName) {
        params.append("repositories[]", strippedRepoName);
      }
    });
  }
  if (filters.selectedRunnerTypes.length > 0) {
    filters.selectedRunnerTypes.forEach((runnerType) =>
      params.append("runner_types[]", runnerType),
    );
  }
  if (filters.selectedStatuses.length > 0) {
    filters.selectedStatuses.forEach((status) =>
      params.append("statuses[]", status),
    );
  }

  return params;
}

export function getRepoChartQueryParams(
  repoName: string,
  filters: RepoLevelChartFilters,
): URLSearchParams {
  const params = new URLSearchParams();
  const strippedRepoName = repoNameWithoutOrgPrefix(repoName);
  if (strippedRepoName) {
    params.append("repositories[]", strippedRepoName);
  }

  if (filters.selectedJobs.length > 0) {
    // We construct a merged representation of the workflow_name and job_name
    // to pass to the API.
    filters.selectedJobs.forEach((job) =>
      params.append("jobs[]", `${job.workflow_name}::${job.job_name}`),
    );
  }
  if (filters.selectedBranches.length > 0) {
    filters.selectedBranches.forEach((branch) =>
      params.append("branches[]", branch),
    );
  }
  if (filters.selectedRunnerTypes.length > 0) {
    filters.selectedRunnerTypes.forEach((runnerType) =>
      params.append("runner_types[]", runnerType),
    );
  }
  if (filters.selectedStatuses.length > 0) {
    filters.selectedStatuses.forEach((status) =>
      params.append("statuses[]", status),
    );
  }

  return params;
}
